import { motion, AnimatePresence } from 'framer-motion';
import { X, Quote, Sparkles } from 'lucide-react';

const SageBackstoryModal = ({ isOpen, onClose, sage, accent, communityName }) => {
  if (!sage) return null;

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={onClose}
          className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center bg-black/80 backdrop-blur-md px-4 py-6 overflow-y-auto"
          data-testid="sage-backstory-modal"
        >
          <motion.div
            initial={{ y: 60, scale: 0.96, opacity: 0 }}
            animate={{ y: 0, scale: 1, opacity: 1 }}
            exit={{ y: 60, scale: 0.96, opacity: 0 }}
            transition={{ type: 'spring', stiffness: 280, damping: 28 }}
            onClick={(e) => e.stopPropagation()}
            className="relative w-full max-w-md rounded-3xl border overflow-hidden my-auto"
            style={{
              borderColor: `${accent}40`,
              background: 'rgba(10, 10, 12, 0.97)',
              boxShadow: `0 0 60px ${accent}30, 0 20px 50px rgba(0,0,0,0.7)`,
            }}
          >
            {/* Decorative top glow */}
            <div className="absolute top-0 left-0 right-0 h-32 pointer-events-none" style={{ background: `radial-gradient(ellipse at 50% 0%, ${accent}25 0%, transparent 70%)` }} />

            {/* Close button */}
            <button
              onClick={onClose}
              className="absolute top-4 right-4 z-10 p-2 rounded-full bg-black/40 hover:bg-black/60 transition-colors"
              data-testid="sage-backstory-close"
              aria-label="Close"
            >
              <X className="w-4 h-4 text-zinc-400" />
            </button>

            <div className="relative px-6 sm:px-8 pt-10 pb-8">
              {/* Epithet pill */}
              <div className="flex justify-center mb-3">
                <span
                  className="px-3 py-1 rounded-full text-[10px] font-heading font-bold tracking-[0.25em] uppercase border"
                  style={{ color: accent, borderColor: `${accent}50`, background: `${accent}10` }}
                  data-testid="sage-epithet"
                >
                  {sage.epithet}
                </span>
              </div>

              {/* Title */}
              <h2 className="font-heading text-2xl sm:text-3xl font-bold text-white text-center mb-1 tracking-wide" data-testid="sage-backstory-title">
                {sage.title}
              </h2>
              <p className="text-zinc-600 text-xs text-center mb-6 tracking-widest uppercase">of {communityName}</p>

              {/* Divider */}
              <div className="flex items-center justify-center gap-3 mb-6">
                <div className="h-px flex-1 max-w-[60px]" style={{ background: `linear-gradient(to right, transparent, ${accent}60)` }} />
                <Sparkles className="w-3.5 h-3.5" style={{ color: accent }} />
                <div className="h-px flex-1 max-w-[60px]" style={{ background: `linear-gradient(to left, transparent, ${accent}60)` }} />
              </div>

              {/* Story */}
              <p className="text-zinc-300 text-sm leading-relaxed mb-7 first-letter:text-3xl first-letter:font-heading first-letter:font-bold first-letter:mr-1 first-letter:float-left first-letter:leading-none" style={{ '--tw-prose-bold': accent }} data-testid="sage-backstory-story">
                {sage.story}
              </p>

              {/* Wisdom quote */}
              <div
                className="relative rounded-2xl p-5 border-l-2"
                style={{ borderLeftColor: accent, background: `linear-gradient(135deg, ${accent}10, transparent)` }}
              >
                <Quote className="absolute -top-2 -left-2 w-5 h-5 p-1 rounded-full" style={{ color: accent, background: 'rgba(10,10,12,1)' }} />
                <p className="text-white text-sm italic leading-relaxed font-light" data-testid="sage-wisdom">
                  &ldquo;{sage.wisdom}&rdquo;
                </p>
                <p className="text-zinc-600 text-[10px] tracking-widest mt-3 text-right">— THE SAGE</p>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default SageBackstoryModal;
